import { isValidElement, useEffect, useRef } from 'react';

import { merge } from 'lodash-es';
import styled from 'styled-components';

import { Text, TextProps } from '@components/text';
import { useLocaleContext } from '@locale/locale-context';

import coreEN from './en.json';
import coreIS from './is.json';
import { createLocaleStore, LocaleCode } from './locale-store';

type CoreTranslationKey = keyof typeof coreEN;

type Translations = {
  [key in string]: string;
};

type GenericTranslationMap = {
  [key in LocaleCode]?: Translations;
};

export type TranslationMap = GenericTranslationMap & {
  en: Translations;
};

export type TranslationsOverride<T extends TranslationMap> = {
  translationsOverride?: { [key in keyof T['en']]?: string };
};

const coreTranslations = {
  en: coreEN as Translations,
  is: coreIS as Translations,
};

let currentTranslations: TranslationMap = coreTranslations;
let currentLocale: LocaleCode = 'en';

let localeStore = createLocaleStore();

type Settings = {
  persistKey?: string;
  translations: TranslationMap;
};

const useSettings = ({ persistKey, translations }: Settings) => {
  const isStoreInitialized = useRef(!Boolean(persistKey));
  if (!isStoreInitialized.current) {
    isStoreInitialized.current = true;
    localeStore = createLocaleStore(persistKey);
  }
  useEffect(() => {
    currentTranslations = merge({}, coreTranslations, translations);
  }, [translations]);
};

export const useTranslations = (props: Settings) => {
  useSettings(props);
  const storedLocale = localeStore.useStore((state) => state.locale);
  if (storedLocale) {
    currentLocale = storedLocale;
  }
};

export const setLocale = <LC extends LocaleCode = LocaleCode>(locale: LC) => {
  currentLocale = locale;
  localeStore.setLocale(locale);
};

export const useLocale = <LC extends LocaleCode = LocaleCode>() => {
  const overrideLocale = useLocaleContext();
  const locale = localeStore.useStore((state) => state.locale);
  return (overrideLocale ?? locale) as LC;
};

export const useTranslate = <TranslationKey, LC extends LocaleCode = LocaleCode>() => {
  const lang = useLocale<LC>();
  const typedTranslate: typeof t<TranslationKey> = (key, ...variables) =>
    translate({ key, variables, translations: currentTranslations, locale: lang });
  return { lang, t: typedTranslate };
};

export const useScopedTranslate = <T extends TranslationMap>(
  translations: T | undefined = {
    en: {},
  } as T,
  translationsOverride?: TranslationsOverride<T>['translationsOverride'],
) => {
  const lang = useLocale<Extract<keyof T, LocaleCode>>();
  return getScopedTranslate(translations, translationsOverride, lang);
};

export const getScopedTranslate = <T extends TranslationMap>(
  translations: T,
  translationsOverride?: TranslationsOverride<T>['translationsOverride'],
  locale?: LocaleCode,
) => {
  const lang = locale ?? localeStore.getLocale();
  const mergedTranslations = merge({}, coreTranslations, translations);
  const scopedTranslate: typeof t<keyof T['en']> = (key, ...variables) =>
    translationsOverride?.[key] ??
    translate({ key, variables, translations: mergedTranslations, locale: lang });
  const styled = (
    key: keyof T['en'],
    variables: VariableRendererProps['variables'],
  ): ReturnType<typeof styledTranslate> =>
    styledTranslate({ key, variables, translations: mergedTranslations, locale: lang });
  return { lang, t: scopedTranslate, styledTranslate: styled };
};

type TranslationVariables = {
  [key in string]: string | number;
};

type TranslateProps<TranslationKey> = {
  key: CoreTranslationKey | TranslationKey;
  variables: TranslationVariables[] | string[];
  translations: TranslationMap;
  locale?: LocaleCode;
};

const translate = <TranslationKey,>({
  key,
  variables,
  translations,
  locale = currentLocale,
}: TranslateProps<TranslationKey>) => {
  let text = translations[locale]?.[key as keyof Translations];
  if (!text) {
    console.error(`Missing translation for string: ${key}, in language: ${locale}`);
    if (locale === 'en') {
      return '';
    }

    text = translations.en[key as keyof Translations];
    if (!text) {
      console.error(`Missing translation for string: ${key}, in language: en`);
      return '';
    }
  }

  if (!variables.length) {
    return text;
  }

  if (typeof variables[0] === 'string') {
    for (let i = 0, n = variables.length; i < n; i++) {
      text = text.replace(`{${i}}`, variables[i] as string);
    }
    return text;
  }

  const variableMap = variables[0] as TranslationVariables;
  const variableKeys = Object.keys(variableMap);

  for (let i = 0, n = variableKeys.length; i < n; i++) {
    const variableKey = variableKeys[i];
    text = text.replaceAll(`{${variableKey}}`, variableMap[variableKey] as string);
  }

  return text;
};

export const t = <TranslationKey,>(
  key: TranslateProps<TranslationKey>['key'],
  ...variables: TranslateProps<TranslationKey>['variables']
): string => translate<TranslationKey>({ key, variables, translations: currentTranslations });

const VariableRendererContainer = styled.div({
  overflowWrap: 'break-word',
  whiteSpace: 'pre-wrap',
});

type VariableRendererProps = {
  text: string;
  variables: { [key: string]: TextProps | string | JSX.Element };
};

const VariableRenderer = ({ text, variables }: VariableRendererProps): JSX.Element => {
  const parts = text.split(/{(.*?)}/g);

  const renderedText = parts.map((part, index) => {
    // Odd numbers are our variables, the evens are text
    if (index % 2 === 1) {
      const variable = variables[part];
      if (typeof variable === 'object') {
        if (isValidElement(variable)) {
          const elementWithKey = { ...variable, key: index };
          return elementWithKey;
        }
        const { style = {}, ...textProps } = variable as TextProps;
        style.display = style.display ?? 'inline-block';
        return <Text key={index} style={style} {...textProps} />;
      }
      return <span key={index}>{variable}</span>;
    }
    return <span key={index}>{part}</span>;
  });

  return <VariableRendererContainer>{renderedText}</VariableRendererContainer>;
};

type StyledTranslateProps<TranslationKey> = Omit<TranslateProps<TranslationKey>, 'variables'> & {
  variables: VariableRendererProps['variables'];
};

export const styledTranslate = <TranslationKey,>({
  key,
  variables,
  translations,
  locale = currentLocale,
}: StyledTranslateProps<TranslationKey>): JSX.Element => {
  const translated = translate<TranslationKey>({
    key,
    variables: [],
    translations,
    locale,
  });

  return <VariableRenderer text={translated} variables={variables} />;
};
